import React, { useState, useRef, useEffect } from "react";
import { useSettings, useCustomer } from "hooks/useStore";
import SearchInput from "./input";
import SearchResult from "./results";
import useFocusTrap from "hooks/useFocusTrap";
import fetchSearchResults from "services/api/search";
import { debounce } from "lodash";
import { trackViewSearchResults, trackSearchTerm } from "analytics";
import clsx from "clsx/lite";
import useToggle from "hooks/useToggle";
import SearchButton from "./button";
import useClickOutside from "hooks/useClickOutside";
import { useMediaQuery } from "react-responsive";
import { SCREEN_SIZES } from "utils/constants";

// Debounced search function outside the component to avoid re-creation
const performSearch = debounce(async (query, setResults, setLoading, customer) => {
	setLoading(true);
	try {
		const resp = await fetchSearchResults(query, true, 9);
		setResults(resp);
		trackViewSearchResults(resp, query, customer, "quick", "products");
	} catch (error) {
		console.error("Error fetching search results:", error);
	} finally {
		setLoading(false);
	}
}, 300);

const Search = ({ data = {}, isEmbedded = false, isModule = false, variation2 = false, embedHeader }) => {
	const { isSearchOpen, toggleSearch } = useSettings(["isSearchOpen", "toggleSearch"]);
	const customer = useCustomer(["id", "email", "tags", "defaultAddress", "isAuthenticated"]);

	const [query, setQuery] = useState("");
	const [searchResults, setSearchResults] = useState({ terms: [], products: [], blogs: [] });
	const [loading, setLoading] = useState(false);
	const [isInputFocused, toggleInputFocus] = useToggle();

	const { popularTerms = [], popularProducts = [] } = data;

	const searchRef = useRef(null);
	const isMobile = useMediaQuery({ maxWidth: SCREEN_SIZES.sm });
	const searchWrapperRef = useClickOutside(() => {
		if (!isMobile) return;
		if (isInputFocused) toggleInputFocus();
		if (query) setQuery("");
		if (isSearchOpen) toggleSearch();
	});

	useFocusTrap(isSearchOpen, searchRef);

	// Update search results when query changes
	useEffect(() => {
		if (!query.trim()) {
			setSearchResults({ terms: [], products: [], blogs: [] });
			return;
		}

		performSearch(query, setSearchResults, setLoading, customer);

		const timer = setTimeout(() => trackSearchTerm(query, "products"), 2000);
		return () => clearTimeout(timer);
	}, [query, customer]);

	// Clear input and results
	const clear = () => {
		setQuery("");
		setSearchResults({ terms: [], products: [], blogs: [] });
	};

	// Close input focus on outside click
	useEffect(() => {
		const handleClickOutside = (e) => {
			if (isInputFocused && searchRef.current && !searchRef.current.contains(e.target)) {
				toggleInputFocus();
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [isInputFocused, toggleInputFocus]);

	const showResults = query.length > 0 || isInputFocused || isSearchOpen;
	const showSearchPanel = isSearchOpen || isEmbedded;

	return (
		<div ref={searchRef} className={clsx("flex", variation2 && "search-variation-2")}>
			{!variation2 && <SearchButton embedHeader={embedHeader} />}
			{variation2 && (
				<SearchInput
					value={query}
					clear={clear}
					toggleInputFocus={toggleInputFocus}
					isInputFocused={isInputFocused}
					onChange={setQuery}
					variation2
					embedHeader={embedHeader}
				/>
			)}

			{showSearchPanel && (
				<div className={clsx(isEmbedded ? "w-full" : "search-container", isModule && "module-search")}>
					<div
						className={clsx("standard-section-width mx-auto", !isEmbedded && "horizontal-padding")}
						ref={searchWrapperRef}
					>
						<div className="md:mr-10 md:mb-40 md:ml-32">
							<SearchInput
								value={query}
								clear={clear}
								toggleInputFocus={toggleInputFocus}
								isInputFocused={isInputFocused}
								onChange={setQuery}
								isModule={isModule}
								results={searchResults}
							/>
							{showResults && (
								<SearchResult
									query={query}
									results={searchResults}
									loading={loading}
									popularData={{ popularProducts, popularTerms }}
									isMobile={isMobile}
								/>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default Search;
